// macadamia-app/src/macadamia-core/components/widgets/utils/blockIdUtils.ts

/**
 * Utilitaires pour la gestion des IDs de blocs et items
 *
 * Les listes utilisent des IDs composites pour tracker les références par item :
 * Format: {blockId}-item-{itemId}
 */

/**
 * Génère un ID composite pour un item de liste
 * @param blockId - ID du bloc liste parent
 * @param itemId - ID de l'item dans la liste
 * @returns ID composite au format {blockId}-item-{itemId}
 */
export function generateListItemBlockId(blockId: string, itemId: string): string {
  return `${blockId}-item-${itemId}`;
}

/**
 * Vérifie si un blockId est un ID composite (item de liste)
 * @param blockId - L'ID à vérifier
 * @returns true si c'est un ID composite, false sinon
 */
export function isCompositeBlockId(blockId: string): boolean {
  return blockId.includes('-item-');
}

/**
 * Parse un ID composite pour extraire le blockId parent et l'itemId
 * @param compositeId - L'ID composite à parser
 * @returns Object avec parentBlockId et itemId, ou null si ce n'est pas un ID composite
 */
export function parseCompositeBlockId(compositeId: string): { parentBlockId: string; itemId: string } | null {
  if (!isCompositeBlockId(compositeId)) {
    return null;
  }

  const parts = compositeId.split('-item-');
  if (parts.length !== 2) {
    return null;
  }

  return {
    parentBlockId: parts[0],
    itemId: parts[1]
  };
}

/**
 * Extrait le blockId racine d'un ID (composite ou simple)
 * @param blockId - L'ID (composite ou simple)
 * @returns Le blockId racine (sans la partie -item-{itemId})
 */
export function getRootBlockId(blockId: string): string {
  if (isCompositeBlockId(blockId)) {
    const parsed = parseCompositeBlockId(blockId);
    return parsed?.parentBlockId || blockId;
  }
  return blockId;
}
